import { useState } from "react";

// ── Brand colours ────────────────────────────────────────────────
const C = {
  purple:   "#5B2D9E",
  purpleDk: "#3D1A72",
  lime:     "#C6F135",
  offWhite: "#F7F7F7",
  muted:    "#888",
  cardBg:   "#1C0F38",
};

const TIME_SLOTS = {
  weekday:  ["5:00 PM – 7:00 PM","6:00 PM – 8:00 PM"],
  friday:   ["5:00 PM – 7:00 PM","6:00 PM – 8:00 PM","8:30 PM – 10:30 PM"],
  saturday: ["5:00 PM – 7:00 PM","6:00 PM – 8:00 PM","8:30 PM – 10:30 PM"],
  sunday:   ["2:00 PM – 4:00 PM","5:00 PM – 7:00 PM"],
};

function getSlotsForDate(d) {
  if (!d) return [];
  const day = new Date(d + "T00:00:00").getDay();
  if (day === 5) return TIME_SLOTS.friday;
  if (day === 6) return TIME_SLOTS.saturday;
  if (day === 0) return TIME_SLOTS.sunday;
  return TIME_SLOTS.weekday;
}

// ── EmailJS config — fill these in ──────────────────────────────
const EJS = {
  serviceId:        "service_m1u19lk",
  adminTemplate:    "template_5vtxn4l",
  customerTemplate: "template_s40nd1n",
  publicKey:        "UuRZTibToVx7q6O6A",
};

async function sendEmail(templateId, params) {
  const res = await fetch("https://api.emailjs.com/api/v1.0/email/send", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      service_id:  EJS.serviceId,
      template_id: templateId,
      user_id:     EJS.publicKey,
      template_params: params,
    }),
  });
  if (!res.ok) throw new Error("Email failed");
}

// ── Logo ─────────────────────────────────────────────────────────
function PardyLogo({ size = 60 }) {
  const w = size * 1.3;
  return (
    <svg viewBox="0 0 260 200" width={w} height={size} style={{ display:"block", overflow:"visible" }}>
      <defs>
        <filter id="ds" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="4" stdDeviation="5" floodColor="#000" floodOpacity="0.45"/>
        </filter>
        <filter id="ts">
          <feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.5"/>
        </filter>
      </defs>
      <path d="M28,72 Q18,40 50,25 Q80,10 130,12 Q180,10 210,25 Q242,40 232,72 Q245,95 232,128 Q218,160 180,172 Q155,182 130,182 Q105,182 80,172 Q42,160 28,128 Q15,95 28,72Z"
        fill={C.purple} filter="url(#ds)" />
      <text x="130" y="95" textAnchor="middle"
        fontFamily="'Arial Black','Arial Bold',Impact,sans-serif"
        fontWeight="900" fontSize="72" fill={C.lime}
        stroke="#2A0A60" strokeWidth="5" paintOrder="stroke"
        filter="url(#ts)" letterSpacing="-1">PARDY</text>
      <text x="130" y="158" textAnchor="middle"
        fontFamily="'Arial Black','Arial Bold',Impact,sans-serif"
        fontWeight="900" fontSize="50" fill={C.lime}
        stroke="#2A0A60" strokeWidth="4" paintOrder="stroke"
        filter="url(#ts)" letterSpacing="1">CENTRAL</text>
    </svg>
  );
}

// ── Form controls ────────────────────────────────────────────────
const iBase = {
  width:"100%", padding:"11px 14px", borderRadius:10,
  border:"2px solid #ddd", background:"#fff",
  fontSize:14, outline:"none", boxSizing:"border-box",
  color:"#111", transition:"border 0.15s",
};

function Field({ label, req, err, children }) {
  return (
    <div style={{ marginBottom:16 }}>
      <label style={{ display:"block", fontWeight:800, fontSize:13, color:C.purple, marginBottom:4 }}>
        {label}{req && <span style={{ color:"#EF4444" }}> *</span>}
      </label>
      {children}
      {err && <div style={{ color:"#EF4444", fontSize:12, marginTop:3 }}>{err}</div>}
    </div>
  );
}

function TInput({ label, req, type="text", value, onChange, placeholder, err }) {
  const [foc, setFoc] = useState(false);
  return (
    <Field label={label} req={req} err={err}>
      <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        style={{ ...iBase, border:`2px solid ${foc ? C.purple : "#ddd"}` }}
        onFocus={()=>setFoc(true)} onBlur={()=>setFoc(false)} />
    </Field>
  );
}

function TSelect({ label, req, value, onChange, options, err }) {
  const [foc, setFoc] = useState(false);
  return (
    <Field label={label} req={req} err={err}>
      <select value={value} onChange={e=>onChange(e.target.value)}
        style={{ ...iBase, border:`2px solid ${foc ? C.purple : "#ddd"}` }}
        onFocus={()=>setFoc(true)} onBlur={()=>setFoc(false)}>
        <option value="">Select...</option>
        {options.map(o=><option key={o} value={o}>{o}</option>)}
      </select>
    </Field>
  );
}

function TArea({ label, value, onChange, placeholder }) {
  const [foc, setFoc] = useState(false);
  return (
    <Field label={label}>
      <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={3}
        style={{ ...iBase, resize:"vertical", border:`2px solid ${foc ? C.purple : "#ddd"}` }}
        onFocus={()=>setFoc(true)} onBlur={()=>setFoc(false)} />
    </Field>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background:"#fff", borderRadius:16, padding:"22px 22px 8px", marginBottom:20,
      boxShadow:"0 2px 14px rgba(91,45,158,0.09)", borderTop:`4px solid ${C.purple}` }}>
      <h3 style={{ margin:"0 0 18px", fontSize:15, fontWeight:900, color:C.purple }}>{title}</h3>
      {children}
    </div>
  );
}

// ── BOOKING FORM ─────────────────────────────────────────────────
const EMPTY = {
  name:"", phone:"", email:"", eventType:"", category:"",
  childName:"", childAge:"", date:"", timeSlot:"", customTime:"",
  guests:"", org:"", notes:"", waiver:false,
};

function BookingForm() {
  const [f, setF]           = useState(EMPTY);
  const [errs, setErrs]     = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone]     = useState(false);
  const [apiErr, setApiErr] = useState("");
  const set = k => v => setF(p=>({...p,[k]:v}));

  const validate = () => {
    const e={};
    if (!f.name)  e.name="Required";
    if (!f.phone) e.phone="Required";
    if (!f.email||!f.email.includes("@")) e.email="Valid email required";
    if (!f.eventType) e.eventType="Required";
    if (!f.category)  e.category="Required";
    if (!f.date)   e.date="Required";
    if (!f.timeSlot&&!f.customTime) e.time="Please choose a time slot or enter a custom time";
    if (!f.guests) e.guests="Required";
    if (!f.waiver) e.waiver="You must agree to the waiver before submitting";
    return e;
  };

  const submit = async () => {
    const e = validate(); setErrs(e);
    if (Object.keys(e).length) return;
    setLoading(true); setApiErr("");

    const time = f.timeSlot || f.customTime;
    const ref  = `PC-${Date.now().toString().slice(-6)}`;

    try {
      // ── Email 1: to pardycentralnl@gmail.com ──────────────────
      await sendEmail(EJS.adminTemplate, {
        ref,
        customer_name:  f.name,
        customer_email: f.email,
        customer_phone: f.phone,
        event_type:     f.eventType,
        category:       f.category,
        child_info:     f.childName ? `${f.childName}, Age ${f.childAge}` : "N/A",
        date:           f.date,
        time_slot:      time,
        guests:         f.guests,
        org:            f.org || "N/A",
        notes:          f.notes || "None",
        to_email:       "pardycentralnl@gmail.com",
      });

      // ── Email 2: confirmation to customer ─────────────────────
      await sendEmail(EJS.customerTemplate, {
        ref,
        to_name:    f.name,
        to_email:   f.email,
        event_type: f.eventType,
        date:       f.date,
        time_slot:  time,
        guests:     f.guests,
      });

      setDone(true);
    } catch {
      setApiErr("Something went wrong sending your request. Please call us at 709-572-2588.");
    }
    setLoading(false);
  };

  const slots = getSlotsForDate(f.date);

  // ── Success screen ───────────────────────────────────────────
  if (done) return (
    <div style={{ textAlign:"center", padding:"60px 24px", maxWidth:520, margin:"0 auto" }}>
      <div style={{ display:"flex", justifyContent:"center", marginBottom:20 }}><PardyLogo size={100} /></div>
      <div style={{ fontSize:56, marginBottom:8 }}>🎉</div>
      <h2 style={{ color:C.purple, fontSize:28, fontWeight:900, margin:"0 0 12px" }}>Request Received!</h2>
      <p style={{ color:"#444", fontSize:16, lineHeight:1.7, marginBottom:24 }}>
        Thanks <strong>{f.name}</strong>! Your booking request has been sent to Pardy Central.<br/>
        Check your inbox at <strong>{f.email}</strong> for your confirmation.
      </p>
      <div style={{ background:`linear-gradient(135deg,${C.purple},${C.purpleDk})`, borderRadius:16, padding:"22px 24px", color:"#fff", fontSize:14, lineHeight:2, marginBottom:24, textAlign:"left" }}>
        <div style={{ fontWeight:900, color:C.lime, marginBottom:6, fontSize:15 }}>⏭ What happens next?</div>
        <div>1. We'll review your request and reply to your email</div>
        <div>2. If approved, you'll be asked to send a <strong style={{color:C.lime}}>$100 deposit via EMT</strong></div>
        <div>3. EMT to: <strong style={{color:C.lime}}>pardycentralnl@gmail.com</strong></div>
        <div style={{ opacity:0.6, fontSize:12, marginTop:6 }}>Booking is only confirmed after deposit is received.</div>
      </div>
      <p style={{ color:C.muted, fontSize:14 }}>
        Questions? Call <a href="tel:7095722588" style={{ color:C.purple, fontWeight:800 }}>709-572-2588</a>
      </p>
      <button onClick={()=>{ setF(EMPTY); setDone(false); }}
        style={{ marginTop:20, background:C.purple, color:C.lime, border:`2px solid ${C.lime}`, borderRadius:12, padding:"12px 32px", fontWeight:900, fontSize:16, cursor:"pointer" }}>
        Book Another Event
      </button>
    </div>
  );

  // ── Form ─────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth:640, margin:"0 auto", padding:"0 16px 48px" }}>
      {/* Hero */}
      <div style={{ textAlign:"center", padding:"32px 0 24px" }}>
        <div style={{ display:"flex", justifyContent:"center", marginBottom:12 }}><PardyLogo size={100} /></div>
        <h1 style={{ margin:"0 0 8px", fontSize:28, fontWeight:900, color:C.purple }}>Book Your Event</h1>
        <p style={{ color:C.muted, fontSize:15, marginBottom:14 }}>Fill out the form — we'll get back to you shortly! 🎊</p>
        <div style={{ display:"flex", justifyContent:"center", gap:8, flexWrap:"wrap" }}>
          {["🎂 Birthday Parties","🏒 Team Events","🎊 Private Rentals"].map(t=>(
            <span key={t} style={{ background:C.purple, color:C.lime, borderRadius:20, padding:"5px 14px", fontSize:12, fontWeight:800 }}>{t}</span>
          ))}
        </div>
      </div>

      {/* Pricing */}
      <div style={{ background:`linear-gradient(135deg,${C.purple},${C.purpleDk})`, borderRadius:16, padding:"16px 22px", marginBottom:28, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontWeight:900, fontSize:22, color:C.lime }}>$250 + HST</div>
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.8)" }}>Full venue · 2-hour booking</div>
        </div>
        <div style={{ textAlign:"right", fontSize:13, color:"rgba(255,255,255,0.8)" }}>
          <div style={{ color:C.lime, fontWeight:800 }}>$100 deposit to confirm</div>
          <div>via EMT after approval</div>
        </div>
      </div>

      {/* Contact */}
      <Section title="👤 Contact Information">
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
          <TInput label="Full Name"    req value={f.name}  onChange={set("name")}  placeholder="Jane Smith"      err={errs.name} />
          <TInput label="Phone Number" req type="tel" value={f.phone} onChange={set("phone")} placeholder="709-555-0100" err={errs.phone} />
        </div>
        <TInput label="Email Address" req type="email" value={f.email} onChange={set("email")} placeholder="jane@example.com" err={errs.email} />
      </Section>

      {/* Event */}
      <Section title="🎉 Event Details">
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
          <TSelect label="Event Type"     req value={f.eventType} onChange={set("eventType")} err={errs.eventType}
            options={["Birthday Party","Team Party","Team Builder","Private Event","Other"]} />
          <TSelect label="Event Category" req value={f.category}  onChange={set("category")}  err={errs.category}
            options={["Kids Birthday","Sports Team","School Group","Staff Party","Private Rental"]} />
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
          <TInput label="Child's Name" value={f.childName} onChange={set("childName")} placeholder="Optional" />
          <TInput label="Child's Age"  value={f.childAge}  onChange={set("childAge")}  placeholder="Optional" />
        </div>
        <TInput label="Organization / Team Name" value={f.org} onChange={set("org")} placeholder="Optional" />
        <TInput label="Number of Guests" req type="number" value={f.guests} onChange={set("guests")} placeholder="e.g. 25" err={errs.guests} />
      </Section>

      {/* Date & Time */}
      <Section title="📅 Date & Time">
        <TInput label="Preferred Date" req type="date" value={f.date}
          onChange={v=>{ set("date")(v); set("timeSlot")(""); }} err={errs.date} />
        {f.date && (
          <Field label="Preferred Time Slot" req err={errs.time}>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
              {slots.map(s=>(
                <button key={s} type="button" onClick={()=>{ set("timeSlot")(s); set("customTime")(""); }}
                  style={{ padding:"10px 16px", borderRadius:10, fontWeight:800, fontSize:13, cursor:"pointer",
                    border:`2px solid ${f.timeSlot===s ? C.purple : "#ddd"}`,
                    background: f.timeSlot===s ? C.purple : "#fff",
                    color: f.timeSlot===s ? C.lime : C.purple }}>
                  {s}
                </button>
              ))}
              <button type="button" onClick={()=>set("timeSlot")("")}
                style={{ padding:"10px 16px", borderRadius:10, fontWeight:800, fontSize:13, cursor:"pointer",
                  border:`2px solid ${!f.timeSlot ? C.purple : "#ddd"}`,
                  background: !f.timeSlot ? C.purpleDk : "#fff",
                  color: !f.timeSlot ? C.lime : C.muted }}>
                🕐 Custom
              </button>
            </div>
          </Field>
        )}
        {!f.timeSlot && f.date && (
          <TInput label="Custom Time Request" value={f.customTime} onChange={set("customTime")} placeholder="e.g. 3:00 PM – 5:00 PM" />
        )}
        {!f.date && <p style={{ color:C.muted, fontSize:13, marginTop:-8, marginBottom:12 }}>Select a date to see available time slots.</p>}
      </Section>

      {/* Notes */}
      <Section title="📝 Notes & Special Requests">
        <TArea label="Anything we should know?" value={f.notes} onChange={set("notes")} placeholder="Theme, accessibility needs, decorations, etc." />
      </Section>

      {/* Waiver */}
      <div style={{ background:"#FAF5FF", border:`2px solid ${errs.waiver ? "#EF4444" : C.purple+"33"}`, borderRadius:14, padding:"18px 20px", marginBottom:24 }}>
        <label style={{ display:"flex", alignItems:"flex-start", gap:12, cursor:"pointer" }}>
          <input type="checkbox" checked={f.waiver} onChange={e=>set("waiver")(e.target.checked)}
            style={{ width:20, height:20, marginTop:1, accentColor:C.purple, flexShrink:0 }} />
          <span style={{ fontSize:14, color:"#333", lineHeight:1.6 }}>
            I agree to the{" "}
            <a href="https://pardycentral.com/waiver-of-liability-and-release/" target="_blank" rel="noreferrer"
              style={{ color:C.purple, fontWeight:800, textDecoration:"underline" }}>
              Waiver of Liability and Release
            </a>
            <span style={{ color:"#EF4444" }}> *</span>
          </span>
        </label>
        {errs.waiver && <div style={{ color:"#EF4444", fontSize:12, marginTop:6, marginLeft:32 }}>{errs.waiver}</div>}
      </div>

      {apiErr && (
        <div style={{ background:"#FEE2E2", border:"1px solid #EF4444", borderRadius:10, padding:"12px 16px", marginBottom:16, color:"#991B1B", fontSize:14 }}>
          ⚠️ {apiErr}
        </div>
      )}

      <button onClick={submit} disabled={loading}
        style={{ width:"100%", background: loading ? "#9B7ED4" : `linear-gradient(135deg,${C.purple},${C.purpleDk})`,
          color:C.lime, border:`3px solid ${C.lime}`, borderRadius:16, padding:"17px",
          fontWeight:900, fontSize:20, cursor: loading ? "not-allowed" : "pointer", letterSpacing:1,
          boxShadow:`0 6px 24px ${C.purple}55` }}>
        {loading ? "⏳ Sending your request..." : "🎉 SUBMIT BOOKING REQUEST"}
      </button>
      <p style={{ textAlign:"center", color:C.muted, fontSize:13, marginTop:14 }}>
        Questions? Call <a href="tel:7095722588" style={{ color:C.purple, fontWeight:800 }}>709-572-2588</a>
      </p>
    </div>
  );
}

function NavBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      style={{ background: active ? C.lime : "transparent", color: active ? C.purple : "rgba(255,255,255,0.7)",
        border:"none", borderRadius:8, padding:"7px 18px", fontWeight:900, fontSize:13, cursor:"pointer" }}>
      {children}
    </button>
  );
}

// ── ROOT ─────────────────────────────────────────────────────────
export default function App() {
  return (
    <div style={{ minHeight:"100vh", background:C.offWhite, fontFamily:"'Segoe UI',system-ui,sans-serif" }}>
      <div style={{ background:C.purpleDk, padding:"0 20px", display:"flex", justifyContent:"space-between", alignItems:"center", height:60, borderBottom:`3px solid ${C.lime}` }}>
        <PardyLogo size={44} />
        <a href="tel:7095722588" style={{ color:C.lime, fontWeight:800, fontSize:14, textDecoration:"none" }}>
          📞 709-572-2588
        </a>
      </div>
      <BookingForm />
    </div>
  );
}